import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, BookOpen, CheckCircle2 } from "lucide-react";

const MODULES = [
  "M1: Foundations of ODR in India",
  "M2: Mediation Craft & Ethics",
  "M3: Arbitration under the 1996 Act",
  "M4: The Mediation Act, 2023",
  "M5: Online Hearings & Evidence",
  "M6: Drafting Awards & Settlements",
  "M7: eSulah Platform Tooling",
];

const QUIZ = [
  { q: "Mediated settlements are enforceable under?", opts: ["Mediation Act, 2023", "Indian Penal Code", "Companies Act"], a: 0 },
  { q: "Arbitral awards in India are governed by?", opts: ["Mediation Act", "Arbitration & Conciliation Act, 1996", "CPC"], a: 1 },
  { q: "On eSulah, evidence is auto-classified using?", opts: ["Manual upload only", "OCR-style classification", "Random tagging"], a: 1 },
];

export default function NeutralCourse() {
  const [done, setDone] = useState(new Array(MODULES.length).fill(false));
  const [answers, setAnswers] = useState({});
  const [profile, setProfile] = useState(null);
  const [profForm, setProfForm] = useState({ qualification: "", bar_id: "", districts: "", languages: "", specializations: "" });

  useEffect(() => {
    api.get("/neutrals/me/profile").then(r => {
      setProfile(r.data);
      setProfForm({
        qualification: r.data?.qualification || "",
        bar_id: r.data?.bar_id || "",
        districts: (r.data?.districts || []).join(", "),
        languages: (r.data?.languages || []).join(", "),
        specializations: (r.data?.specializations || []).join(", "),
      });
    });
  }, []);

  const allDone = done.every(Boolean);
  const quizScore = QUIZ.reduce((s, q, i) => s + (answers[i] === q.a ? 1 : 0), 0);
  const quizPassed = quizScore === QUIZ.length;

  const saveProfile = async () => {
    try {
      await api.put("/neutrals/profile", {
        qualification: profForm.qualification,
        bar_id: profForm.bar_id,
        districts: profForm.districts.split(",").map(s => s.trim()).filter(Boolean),
        languages: profForm.languages.split(",").map(s => s.trim()).filter(Boolean),
        specializations: profForm.specializations.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Profile updated");
    } catch { toast.error("Could not save profile"); }
  };

  const completeCourse = async () => {
    try {
      await api.post("/neutrals/me/complete-course");
      toast.success("Course completed. Admin will activate empanelment.");
      const r = await api.get("/neutrals/me/profile");
      setProfile(r.data);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="neutral-course">
      <div>
        <h1 className="font-display text-3xl font-bold text-court flex items-center gap-2"><GraduationCap className="h-7 w-7" /> eSulah ODR Course</h1>
        <p className="text-muted-foreground">Complete all modules and the quiz to unlock empanelment.</p>
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-6 ">
        <h3 className="font-display text-xl text-court mb-3">Profile</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Qualification</Label><Input value={profForm.qualification} onChange={(e) => setProfForm(f => ({...f, qualification: e.target.value}))} data-testid="profile-qualification" /></div>
          <div><Label>Bar Council ID</Label><Input value={profForm.bar_id} onChange={(e) => setProfForm(f => ({...f, bar_id: e.target.value}))} data-testid="profile-bar-id" /></div>
          <div><Label>Districts (comma-separated)</Label><Input value={profForm.districts} onChange={(e) => setProfForm(f => ({...f, districts: e.target.value}))} data-testid="profile-districts" /></div>
          <div><Label>Languages (comma-separated)</Label><Input value={profForm.languages} onChange={(e) => setProfForm(f => ({...f, languages: e.target.value}))} data-testid="profile-languages" /></div>
          <div className="sm:col-span-2"><Label>Specializations (comma-separated)</Label><Input value={profForm.specializations} onChange={(e) => setProfForm(f => ({...f, specializations: e.target.value}))} data-testid="profile-specializations" /></div>
        </div>
        <Button onClick={saveProfile} className="mt-4 rounded-sm bg-primary" data-testid="profile-save">Save profile</Button>
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-6 ">
        <h3 className="font-display text-xl text-court mb-3 flex items-center gap-2"><BookOpen className="h-5 w-5" /> Modules</h3>
        <ul className="space-y-2">
          {MODULES.map((m, i) => (
            <li key={m} className="flex items-center gap-3 p-3 rounded-sm bg-muted/40">
              <Checkbox id={`m-${i}`} checked={done[i]} onCheckedChange={(v) => setDone(d => d.map((x, j) => j === i ? !!v : x))} data-testid={`module-${i}`} />
              <label htmlFor={`m-${i}`} className="text-sm font-medium">{m}</label>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-6 ">
        <h3 className="font-display text-xl text-court mb-3">Quick quiz</h3>
        <ol className="space-y-5">
          {QUIZ.map((q, i) => (
            <li key={i}>
              <div className="font-medium mb-2">{i + 1}. {q.q}</div>
              <div className="space-y-2">
                {q.opts.map((o, j) => (
                  <label key={j} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`quiz-${i}-${j}`}>
                    <input type="radio" name={`q-${i}`} checked={answers[i] === j} onChange={() => setAnswers(a => ({...a, [i]: j}))} />
                    {o}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-4 text-sm text-muted-foreground">Score: {quizScore} / {QUIZ.length}</div>
      </div>

      <div className="bg-primary text-court-foreground rounded-sm p-8 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h3 className="font-display text-2xl">{profile?.course_completed ? "Course completed ✓" : "Ready to graduate?"}</h3>
          <p className="opacity-85 mt-1 text-sm">All modules &amp; quiz passed = course completed. Admin then activates empanelment.</p>
        </div>
        <Button
          disabled={!allDone || !quizPassed || profile?.course_completed}
          onClick={completeCourse}
          className="rounded-sm bg-secondary hover:bg-secondary/90 text-secondary-foreground h-12 px-7"
          data-testid="complete-course"
        >
          {profile?.course_completed ? <><CheckCircle2 className="h-4 w-4 mr-2" /> Completed</> : "Complete course"}
        </Button>
      </div>
    </div>
  );
}
